import { Controller, useForm, FieldValues } from "react-hook-form";
import { Popup } from "components/UI/Popup";
import { InputLabel, TextField } from "@mui/material";
import { ToggleButton } from "components/buttons/ToggleButton";
import "./styles/CreateNewCollectionPopup.scss";
import { useAddUserCollectionMutation } from "queries";
import { toast } from "react-hot-toast";
import { ICreateCollection } from "services/types";

interface ICreateNewCollectionPopupProps {
  isOpen: boolean;
  togglePopup: () => void;
}

export const CreateNewCollectionPopup = ({ isOpen, togglePopup }: ICreateNewCollectionPopupProps) => {
  const { control, handleSubmit } = useForm();
  const addUserCollectionMutation = useAddUserCollectionMutation();

  const onSubmit = async (data: FieldValues) => {
    const dataToSend: ICreateCollection = {
      published: !data.published,
      description: data.description || null,
      folderName: data.folderName
    };

    try {
      await addUserCollectionMutation.mutateAsync(dataToSend);
      toast.success(
        "Successfully created.",
        { duration: 5000 }
      );
    } catch (e) {
      toast.error(
        "Something went wrong. Please try later.",
        { duration: 5000 }
      );
    } finally {
      togglePopup();
    }
  };

  const btnProps = {
    variant: 1,
    title: "Create",
    formId: "create-collection-form",
    btnType: "submit"
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={togglePopup}
      title="Create a Collection"
      popupClass="CreateNewCollectionPopup"
      btnProps={btnProps}
    >
      <form onSubmit={handleSubmit(onSubmit)} id="create-collection-form">
        <InputLabel
          sx={{
            fontSize: "12px",
            lineHeight: "18px",
            fontWeight: 500,
            fontFamily: "Poppins",
            color: "var(--text-dark)",
            opacity: ".8"
          }}
        >
          Collection name
        </InputLabel>
        <Controller
          name="folderName"
          control={control}
          defaultValue=""
          rules={{ required: "Required Field!" }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              sx={{
                ".Mui-focused, & div": {
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)!important",
                    borderWidth: "1px"
                  }
                },
                "& div": {
                  borderRadius: "12px",
                  margin: "0.5rem 0",
                  "& input": {
                    padding: "13px 16px",
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "22px"
                  }
                }
              }}
              fullWidth
              onChange={onChange}
              value={value}
              error={!!error}
            />
          )}
        />
        <InputLabel
          sx={{
            fontSize: "12px",
            lineHeight: "18px",
            fontWeight: 500,
            fontFamily: "Poppins",
            color: "var(--text-dark)",
            opacity: ".8",
            marginTop: "10px"
          }}
        >
          Description
        </InputLabel>
        <Controller
          name="description"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              rows={3}
              multiline
              sx={{
                ".Mui-focused, & div": {
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)!important",
                    borderWidth: "1px"
                  }
                },
                "& div": {
                  borderRadius: "12px",
                  margin: "0.5rem 0",
                  "& textarea": {
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "22px"
                  }
                }
              }}
              fullWidth
              onChange={onChange}
              value={value}
              error={!!error}
            />
          )}
        />

        <div className="CreateNewCollectionPopup-Toggle">
          <p>Make private</p>
          <Controller
            name="published"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <ToggleButton checked={!!value} onChange={onChange}/>
            )}
          />
        </div>
      </form>
    </Popup>
  );
};

